import React, { useMemo } from 'react';
import styled from 'styled-components';
import { useHistory, useLocation } from 'react-router-dom';
import { useSelector } from 'react-redux';
import { classStoreData } from 'src/operations/store';

const Sidebar = styled.nav`
  width: 220px;
  min-width: 220px;
  height: auto;
  min-height: calc(100vh - 64px);
  align-self: stretch;
  padding: 12px;
  background: #ffffff;
  border-right: 1.5px solid rgba(8, 145, 178, 0.14);
  overflow-y: auto;
  position: relative;
  z-index: 2;

  &::-webkit-scrollbar {
    width: 3px;
  }

  &::-webkit-scrollbar-thumb {
    background: rgba(8, 145, 178, 0.22);
    border-radius: 2px;
  }
`;

const SectionTitle = styled.div`
  padding: 8px 8px 4px;
  color: #8fa3bb;
  font-size: 9px;
  font-weight: 700;
  letter-spacing: 0.2em;
  text-transform: uppercase;
`;

const SectionDivider = styled.div`
  height: 1px;
  margin: 8px 0;
  background: rgba(8, 145, 178, 0.14);
`;

const SidebarItem = styled.button`
  width: 100%;
  margin-bottom: 2px;
  padding: 8px 12px;
  border: 0;
  border-radius: 8px;
  background: ${(props) => (props.$active ? 'linear-gradient(135deg, #0891b2, #06b6d4)' : 'transparent')};
  color: ${(props) => (props.$active ? '#ffffff' : props.$disabled ? '#8fa3bb' : '#8fa3bb')};
  font-size: 13px;
  font-weight: ${(props) => (props.$active ? 900 : 700)};
  display: flex;
  align-items: center;
  justify-content: space-between;
  cursor: ${(props) => (props.$disabled ? 'not-allowed' : 'pointer')};
  opacity: ${(props) => (props.$disabled ? 0.45 : 1)};
  transition: all 0.18s ease;

  &:hover {
    background: ${(props) => (props.$disabled ? 'transparent' : props.$active ? 'linear-gradient(135deg, #0891b2, #06b6d4)' : 'rgba(8, 145, 178, 0.08)')};
    color: ${(props) => (props.$disabled ? '#8fa3bb' : props.$active ? '#ffffff' : '#0891b2')};
  }
`;

const SidebarIcon = styled.span`
  width: 20px;
  flex-shrink: 0;
  text-align: center;
  font-size: 15px;
  line-height: 1;
`;

const SidebarLabel = styled.span`
  flex: 1;
  text-align: left;
`;

const MxGrid = styled.div`
  display: flex;
  flex-direction: column;
  gap: 2px;
  padding-bottom: 8px;
`;

const MxSectionTitle = styled.div`
  display: flex;
  align-items: center;
  gap: 8px;
  padding: 8px 8px 6px;
  color: #4a6380;
  font-size: 13px;
  font-weight: 700;
`;

const MxItem = styled.button`
  width: 100%;
  padding: 8px 12px;
  border: 0;
  border-radius: 8px;
  background: ${(props) => (props.$active ? 'linear-gradient(135deg, #0891b2, #06b6d4)' : 'transparent')};
  color: ${(props) => (props.$active ? '#ffffff' : props.$disabled ? '#8fa3bb' : '#8fa3bb')};
  font-size: 13px;
  font-weight: ${(props) => (props.$active ? 900 : 700)};
  display: flex;
  align-items: center;
  justify-content: space-between;
  cursor: pointer;
  /* cursor: ${(props) => (props.$disabled ? 'not-allowed' : 'pointer')}; */
  opacity: ${(props) => (props.$disabled ? 0.45 : 1)};
  transition: all 0.18s ease;

  &:hover {
    background: ${(props) => (props.$disabled ? 'transparent' : props.$active ? 'linear-gradient(135deg, #0891b2, #06b6d4)' : 'rgba(8, 145, 178, 0.08)')};
    color: ${(props) => (props.$disabled ? '#8fa3bb' : props.$active ? '#ffffff' : '#0891b2')};
  }
`;

const MyBadge = styled.span`
  padding: 1px 7px;
  border-radius: 20px;
  background: ${(props) => (props.$active ? 'rgba(255, 255, 255, 0.3)' : 'rgba(8, 145, 178, 0.1)')};
  color: ${(props) => (props.$active ? '#ffffff' : '#0891b2')};
  font-size: 8px;
  font-weight: 900;
`;

const levelList = [1, 2, 3, 4, 5, 6, 7, 8, 9, 10, 11, 12];

export default function LeftMenu({ myLevel = -2, level, setLevel, isLibrary, isMain = false, isSearch = false, isStudent = true }) {
  const companyName = useSelector(classStoreData);
  const history = useHistory();
  const location = useLocation();
  const targetLevel = companyName === 'educloud' ? parseInt(myLevel) : -2;

  const availableLevels = useMemo(() => {
    if (targetLevel !== -2 && companyName === 'educloud') {
      let temp = [-1, 0, 1, 2, 3, 4, 5, 6, 7, 8, 9, 10, 11, 12];
      if (targetLevel === 0) temp = temp.slice(0, 5);
      else if (targetLevel === 2) temp = temp.slice(0, 7);
      else if (targetLevel === 4) temp = temp.slice(4, 9);
      else if (targetLevel === 6) temp = temp.slice(6, 11);
      else if (targetLevel === 9) temp = temp.slice(8);
      return temp;
    }
    return levelList;
  }, [companyName, targetLevel]);

  const canAccess = (lv) => {
    if (companyName !== 'educloud') {
      return true;
    }
    if (!isStudent) {
      return true;
    }
    if (myLevel === -2 || (!myLevel && myLevel !== 0)) {
      return false;
    }
    return availableLevels.includes(parseInt(lv));
  };

  const handleClickLevel = (nextLevel) => {
    if (parseInt(level) === nextLevel && !isLibrary && isSearch) {
      setLevel(null);
      return;
    }
    setLevel(nextLevel);
  };

  const handleMoveLibrary = (nextLevel) => {
    history.push(`/level-up/library?lv=${nextLevel}`);
  };

  return (
    <Sidebar>
      <SectionTitle>Menu</SectionTitle>
      <SidebarItem type="button" $active={isMain} onClick={() => history.push('/level-up')}>
        <SidebarLabel>My Page</SidebarLabel>
      </SidebarItem>
      <SidebarItem type="button" $active={location.pathname?.startsWith?.('/level-up/portfolio')} onClick={() => history.push('/level-up/portfolio')}>
        <SidebarLabel>My Portfolio</SidebarLabel>
      </SidebarItem>

      <SectionDivider />

      <MxSectionTitle>
        <SidebarIcon>{'\u{1F524}'}</SidebarIcon>
        <span>Phonics Level</span>
      </MxSectionTitle>

      <>
        <SidebarItem
          type="button"
          $active={parseInt(level) === -1}
          $disabled={!canAccess(-1)}
          onClick={() => (isSearch ? handleClickLevel(-1) : handleMoveLibrary(-1))}
        >
          <SidebarLabel>Phonics Class</SidebarLabel>
          {parseInt(myLevel) === -1 && <MyBadge>MY</MyBadge>}
        </SidebarItem>
        <SidebarItem
          type="button"
          $active={parseInt(level) === 0}
          $disabled={!canAccess(0)}
          onClick={() => (isSearch ? handleClickLevel(0) : handleMoveLibrary(0))}
        >
          <SidebarLabel>Phonics Readers</SidebarLabel>
          {parseInt(myLevel) === 0 && <MyBadge>MY</MyBadge>}
        </SidebarItem>
      </>

      <SectionDivider />

      <MxSectionTitle>
        <SidebarIcon>{'\u{1F4CA}'}</SidebarIcon>
        <span>mx Level</span>
      </MxSectionTitle>
      <MxGrid>
        {levelList.map((item) =>
          isSearch ? (
            <MxItem
              key={item}
              type="button"
              $active={parseInt(level) === item}
              $disabled={!canAccess(item)}
              onClick={() => handleClickLevel(item)}
            >
              <span>{`mx ${String(item).padStart(2, '0')}`}</span>
              {parseInt(myLevel) === item && <MyBadge $active={parseInt(level) === item}>MY</MyBadge>}
            </MxItem>
          ) : (
            <MxItem
              key={item}
              type="button"
              $active={parseInt(level) === item}
              $disabled={!canAccess(item)}
              onClick={() => handleMoveLibrary(item)}
            >
              <span>{`mx ${String(item).padStart(2, '0')}`}</span>
              {parseInt(myLevel) === item && <MyBadge $active={parseInt(level) === item}>MY</MyBadge>}
            </MxItem>
          ),
        )}
      </MxGrid>
    </Sidebar>
  );
}
